/**
 * Blog Post Detail Page
 * Route: /blog/:slug
 * Renders individual article with rich typography, share options, and related articles
 */

import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  ChevronRight,
  Share2,
  Bookmark,
  Facebook,
  Twitter,
  Copy,
  Check,
  Tag,
} from 'lucide-react';
import { blogPosts, type BlogPost } from '@/data/blogData';
import SEO from '@/components/common/SEO';


/* ─── Format Date ─── */
const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

/* ─── Related Article Card ─── */
const RelatedCard: React.FC<{ post: BlogPost }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="blogpost-related-card group"
    id={`related-${post.id}`}
  >
    <div
      className="blogpost-related-card__cover"
      style={{ background: post.coverGradient }}
    >
      <div className="blogpost-related-card__cover-pattern" />
    </div>
    <div className="blogpost-related-card__body">
      <span className="blogpost-related-card__category">{post.category}</span>
      <h4 className="blogpost-related-card__title">{post.title}</h4>
      <div className="blogpost-related-card__meta">
        <Clock className="h-3 w-3" /> {post.readTime} min read
      </div>
    </div>
  </Link>
);

/* ─── Share Buttons ─── */
const ShareButtons: React.FC<{ title: string }> = ({ title }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(title);

  return (
    <div className="blogpost-share" id="blogpost-share">
      <span className="blogpost-share__label">
        <Share2 className="h-3.5 w-3.5" /> Share
      </span>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="blogpost-share__btn"
        aria-label="Share on Facebook"
      >
        <Facebook className="h-4 w-4" />
      </a>
      <a
        href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="blogpost-share__btn"
        aria-label="Share on Twitter"
      >
        <Twitter className="h-4 w-4" />
      </a>
      <button onClick={handleCopy} className="blogpost-share__btn" aria-label="Copy link">
        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

/* ─── Slug Generator for Headings ─── */
const slugifyHeading = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

/* ─── Table of Contents ─── */
interface TableOfContentsProps {
  content: string[];
  activeId: string;
  onSelect: (id: string) => void;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ content, activeId, onSelect }) => {
  const headings = content
    .filter((block) => block.startsWith('## '))
    .map((block) => {
      const title = block.replace('## ', '').split('\n')[0];
      return {
        title,
        id: slugifyHeading(title),
      };
    });

  if (headings.length === 0) return null;

  return (
    <nav className="blogpost-toc" id="blogpost-toc" aria-label="Table of contents">
      <h4 className="blogpost-toc__title">In This Article</h4>
      <ul className="blogpost-toc__list">
        {headings.map(({ title, id }) => {
          const isActive = activeId === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(id);
                }}
                className={`blogpost-toc__item ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'true' : undefined}
              >
                <ChevronRight className={`blogpost-toc__icon h-3 w-3 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                <span>{title}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/* ─── Render Content Block ─── */
const ContentBlock: React.FC<{ block: string }> = ({ block }) => {
  // Heading block
  if (block.startsWith('## ')) {
    const parts = block.split('\n\n');
    const heading = parts[0].replace('## ', '');
    const body = parts.slice(1).join('\n\n');
    const headingId = slugifyHeading(heading);
    return (
      <div className="blogpost-section" id={headingId}>
        <h2 className="blogpost-section__heading">{heading}</h2>
        {body && <div className="blogpost-section__body">{body}</div>}
      </div>
    );
  }

  // Regular paragraph or checklist
  if (block.includes('✅')) {
    const lines = block.split('\n').filter(Boolean);
    return (
      <div className="blogpost-checklist">
        {lines.map((line, i) => (
          <div key={i} className="blogpost-checklist__item">
            {line}
          </div>
        ))}
      </div>
    );
  }

  // Regular paragraph
  return <p className="blogpost-paragraph">{block}</p>;
};

/* ─── Blog Post Page ─── */
const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);
  const [activeId, setActiveId] = React.useState<string>('');

  const scrollToHeading = React.useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 85;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
      window.history.pushState(null, '', `#${id}`);
      setActiveId(id);
    }
  }, []);

  // Handle hash on initial load
  React.useEffect(() => {
    if (window.location.hash) {
      const hashId = window.location.hash.replace('#', '');
      const timer = setTimeout(() => {
        scrollToHeading(hashId);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [post?.slug, scrollToHeading]);

  // Scroll spy to highlight current section in TOC
  React.useEffect(() => {
    if (!post) return;
    const headingIds = post.content
      .filter((block) => block.startsWith('## '))
      .map((block) => slugifyHeading(block.replace('## ', '').split('\n')[0]));

    if (headingIds.length === 0) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;
      for (let i = headingIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(headingIds[i]);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveId(headingIds[i]);
          return;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  // If not enough related by category, add more from other categories
  if (relatedPosts.length < 3) {
    const morePosts = blogPosts
      .filter((p) => p.id !== post.id && !relatedPosts.find((r) => r.id === p.id))
      .slice(0, 3 - relatedPosts.length);
    relatedPosts.push(...morePosts);
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt,
    author: {
      '@type': 'Person',
      name: post.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'MemAds',
      logo: {
        '@type': 'ImageObject',
        url: `${typeof window !== 'undefined' ? window.location.origin : 'https://memads.in'}/favicon-32x32.png`,
      },
    },
  };

  return (
    <div className="blogpost-page" id="blogpost-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      {/* ── Hero Header ── */}
      <section className="blogpost-hero" style={{ background: post.coverGradient }}>
        <div className="blogpost-hero__pattern" />
        <div className="blogpost-hero__inner">
          <Link to="/blog" className="blogpost-hero__back">
            <ArrowLeft className="h-4 w-4" />
            All Articles
          </Link>
          <div className="blogpost-hero__meta">
            <span className="blogpost-hero__category">{post.category}</span>
            <span className="blogpost-hero__dot">·</span>
            <span className="blogpost-hero__date">
              <Calendar className="h-3.5 w-3.5" /> {formatDate(post.publishedAt)}
            </span>
            <span className="blogpost-hero__dot">·</span>
            <span className="blogpost-hero__read-time">
              <Clock className="h-3.5 w-3.5" /> {post.readTime} min read
            </span>
          </div>
          <h1 className="blogpost-hero__title">{post.title}</h1>
          <p className="blogpost-hero__excerpt">{post.excerpt}</p>
          <div className="blogpost-hero__author">
            <div className="blogpost-hero__avatar">{post.author.avatar}</div>
            <div>
              <div className="blogpost-hero__author-name">{post.author.name}</div>
              <div className="blogpost-hero__author-role">{post.author.role}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Article Content ── */}
      <div className="blogpost-layout">
        {/* Sidebar */}
        <aside className="blogpost-sidebar">
          <TableOfContents content={post.content} activeId={activeId} onSelect={scrollToHeading} />
          <ShareButtons title={post.title} />

          {/* Tags */}
          <div className="blogpost-sidebar-tags" id="blogpost-sidebar-tags">
            <h4 className="blogpost-sidebar-tags__title">
              <Tag className="h-3.5 w-3.5" /> Tags
            </h4>
            <div className="blogpost-sidebar-tags__list">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/blog?search=${encodeURIComponent(tag)}`}
                  className="blogpost-sidebar-tags__tag"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Article */}
        <article className="blogpost-article" id="blogpost-article">
          {post.content.map((block, i) => (
            <ContentBlock key={i} block={block} />
          ))}
        </article>
      </div>

      {/* ── Related Articles ── */}
      {relatedPosts.length > 0 && (
        <section className="blogpost-related" id="blogpost-related">
          <div className="blogpost-related__header">
            <h3 className="blogpost-related__title">Continue Reading</h3>
            <Link to="/blog" className="blogpost-related__all">
              All Articles <ArrowLeft className="h-4 w-4 rotate-180" />
            </Link>
          </div>
          <div className="blogpost-related__grid">
            {relatedPosts.map((p) => (
              <RelatedCard key={p.id} post={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
